import { memo } from 'react';
import { Link } from 'react-router-dom';
import { brightenColor } from 'src/customizable-ui/dashboard/utils/brightenColor.js';
import { CoreBadge } from 'src/RnRCommonComponent';

const MyRecognitionsCard = memo(
	({
		card,
		isEditMode = true,
		isDragging = false,
		isResizing = false,
		isHovered = false,
		myRewards = [],
	}) => {
		const cardData = card.data || {};
		const title = cardData.title || 'My Recognitions';
		const backgroundColor = cardData.backgroundColor || '#ffffff';
		const borderColor = cardData.borderColor || '#dddddd';
		const textColor = cardData.textColor || '#333333';
		const showBorder = cardData.showBorder !== false;
		const borderSize = cardData.borderSize || 1;
		const badgesAmount = cardData.badgesAmount || 2;

		const visibleBadges = myRewards.slice(0, badgesAmount);

		const getCardBorderStyle = () => {
			if (!showBorder && !isEditMode) return 'none';

			if (isDragging || isResizing) {
				return `${borderSize}px solid #74a9ffff`;
			}

			if (isEditMode && isHovered && !isDragging && !isResizing) {
				const brighterBorder = brightenColor(borderColor || '#dddddd', 30);
				return `${borderSize}px solid ${brighterBorder}`;
			}

			if (showBorder) {
				return `${borderSize}px solid ${borderColor}`;
			}

			return 'none';
		};

		const getCardShadow = () => {
			if (isDragging || isResizing) {
				return '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
			}
			if (isEditMode && isHovered && !isDragging && !isResizing) {
				return '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
			}
			return 'none';
		};

		return (
			<div
				className="w-full h-full flex flex-col relative transition-all duration-200"
				style={{
					background: backgroundColor,
					border: getCardBorderStyle(),
					boxShadow: getCardShadow(),
					transform: isDragging ? 'scale(1.05)' : 'scale(1)',
					borderRadius: '14px',
					padding: '16px',
				}}
			>
				<div
					className="d-card-head"
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<h4
						style={{
							fontSize: '14px',
							fontWeight: 700,
							lineHeight: '18px',
							margin: '0 0 10px',
							color: textColor,
							fontFamily: 'Nunito Sans, sans-serif',
						}}
					>
						{title}
					</h4>
					<Link
						to="/my-recognitions"
						style={{
							fontSize: '12px',
							color: '#007bff',
							textDecoration: 'none',
							marginBottom: '10px',
							opacity: 0.8,
							transition: 'opacity 0.2s',
						}}
						onMouseEnter={(e) => (e.target.style.opacity = '1')}
						onMouseLeave={(e) => (e.target.style.opacity = '0.8')}
					>
						View All
					</Link>
				</div>
				<div
					className="flex-1"
					style={{
						overflowY: 'auto',
						overflowX: 'hidden',
						minHeight: 0,
					}}
				>
					{visibleBadges.length > 0 ? (
						<div
							style={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '10px',
								justifyContent: 'center',
							}}
						>
							{visibleBadges.map((badge) => (
								<div
									key={badge.assignmentId || badge.id}
									style={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										width: '120px',
									}}
								>
									<div
										style={{
											width: '100px',
											height: '100px',
											overflow: 'visible',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											flexShrink: 0,
										}}
									>
										<div
											style={{
												transform: 'scale(0.5)',
												transformOrigin: 'center',
											}}
										>
											<CoreBadge
												useTilt={false}
												badgeType={badge.badgeType}
												badgeIcon={badge.iconInfo?.code}
												label={badge.label}
												color={badge.color}
												gradientColor={badge.gradientColor}
												shape={badge.shape}
												badgeRotation={badge.badgeRotation}
												customImageSignedUrl={badge.customImageSignedUrl}
												labelColor={badge.labelColor}
												customIconSignedUrl={badge.customIconSignedUrl}
											/>
										</div>
									</div>
									<span
										style={{
											fontSize: '12px',
											fontWeight: 600,
											color: textColor,
											textAlign: 'center',
											marginTop: '4px',
											fontFamily: 'Nunito Sans, sans-serif',
											lineHeight: '1.3',
											wordBreak: 'break-word',
										}}
									>
										{badge.name || badge.label}
									</span>
									{badge.points > 0 && (
										<span
											style={{
												fontSize: '11px',
												color: textColor,
												opacity: 0.7,
												fontFamily: 'Nunito Sans, sans-serif',
												marginTop: '2px',
											}}
										>
											{badge.points} pts
										</span>
									)}
								</div>
							))}
						</div>
					) : (
						<div
							style={{
								padding: '20px',
								textAlign: 'center',
								color: textColor,
								opacity: 0.6,
								fontSize: '13px',
								fontFamily: 'Nunito Sans, sans-serif',
							}}
						>
							No recognitions yet
						</div>
					)}
				</div>
			</div>
		);
	}
);

MyRecognitionsCard.displayName = 'MyRecognitionsCard';

export default MyRecognitionsCard;
